import * as React from "react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Loader2, Plus, Search, Users, X } from "lucide-react";

import { cn } from "../lib/cn";
import type {
  ActionFieldSpec,
  LoadPeopleOptions,
  PeopleSearchHit,
} from "./ActionFieldEditor";

// Mirrors the BE ``_EMAIL_PATTERN`` (unified_schema_builder) + the FE
// ``EMAIL_RE`` in ActionFieldEditor — kept in lockstep so a complete address
// typed here is also accepted by the BE validator. A fully-typed email is
// always addable as a free recipient, even when it isn't in the People-API
// results (the user must be able to invite anyone).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * RecipientsInput — multi-value chip input with people-search autocomplete.
 *
 * The single shared primitive (DRY rule) that powers EVERY action surface
 * where recipients / attendees are picked: ``ActionFieldEditor``'s
 * ``recipients`` renderer (HITL + manual action drawer) AND
 * ``EmailSendDraftPreview``'s envelope ``A`` / ``Cc`` / ``Ccn`` rows. Per
 * ``feedback_action_forms_account_picker_first.md``: every form scopes the
 * autocomplete to the chosen sender account (Gmail / Outlook / Workspace),
 * never the union of the user's claims.
 *
 * Visual contract (refined June 9, 2026 after user feedback "non capisco
 * come funziona, non vedo suggestion mentre scrivo"):
 *   - leading ``Search`` glyph signals "this is a search field" at a glance.
 *   - chips inside the strip, plain free-text after them; ``Backspace``
 *     removes the trailing chip when the input is empty.
 *   - dropdown opens on focus with a "Suggerimenti — {account}" header so
 *     the operator instantly understands which directory the hits come
 *     from. Top-of-mind hits show before the first keystroke (the BE
 *     accepts empty ``q``).
 *   - amber fallback banner when the autocomplete CAN'T fire (no
 *     ``loadPeopleOptions`` wired by the host OR no ``connectionId``
 *     picked yet upstream): explains WHY the dropdown is silent and how
 *     to recover, instead of silently degrading to plain text — which
 *     reads as broken.
 *   - inline ``Loader2`` spinner inside the chip strip while a search is
 *     in flight: visible feedback during the 220 ms debounce window.
 */

export interface RecipientsInputProps {
  /** Current list of email addresses. */
  readonly value: readonly string[];
  /** Emitted whenever the list changes (chip added, chip removed). */
  readonly onChange: (next: string[]) => void;
  /**
   * Stable id for the ``<input>`` so a sibling ``<label htmlFor>`` can wire
   * itself up. Optional — falls back to an internal id if omitted.
   */
  readonly id?: string;
  /** Accessible label used for screen readers when no visible ``<label>``. */
  readonly ariaLabel?: string;
  /**
   * Placeholder shown when nothing is committed yet. The default adapts
   * to whether the autocomplete is active or in free-text fallback mode.
   */
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly invalid?: boolean;
  /**
   * Async people-search transport. When omitted the component degrades
   * to a plain free-text chip input (still editable). Mirrors the
   * ``LoadPeopleOptions`` contract used by ``ActionFieldEditor``.
   */
  readonly loadPeopleOptions?: LoadPeopleOptions;
  /**
   * Per-capability account-picker value (mailbox / calendar / workspace
   * ID). When ``null`` the people-search is NOT fired and the amber
   * fallback banner is shown explaining why — the host must surface a
   * way for the user to pick the sender first.
   */
  readonly connectionId?: string | null;
  /**
   * Forwarded into ``loadPeopleOptions`` so the parent can inspect
   * field-specific hints (label, helper text, etc.). Optional — the
   * primitive synthesises a minimal spec when the caller doesn't have one.
   */
  readonly field?: ActionFieldSpec;
  /** Extra wrapper classes. */
  readonly className?: string;
}

export const RecipientsInput: React.FC<RecipientsInputProps> = ({
  value,
  onChange,
  id,
  ariaLabel,
  placeholder,
  disabled,
  readOnly,
  invalid,
  loadPeopleOptions,
  connectionId,
  field,
  className,
}) => {
  const { i18n } = useLingui();
  const reactId = React.useId();
  const fieldId = id ?? `recipients-${reactId}`;
  const selected = React.useMemo(() => [...value], [value]);
  const [draft, setDraft] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [hits, setHits] = React.useState<readonly PeopleSearchHit[]>([]);
  // ``activeIdx`` is the WAI-ARIA "active descendant" — the option a
  // keyboard user is hovering with ArrowUp / ArrowDown. ``-1`` means no
  // option is currently highlighted (Enter falls back to the free-text
  // commit path). Reset to ``-1`` whenever the visible hits collection
  // changes so the highlight never points to a stale row.
  const [activeIdx, setActiveIdx] = React.useState<number>(-1);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const fallbackOnly =
    !loadPeopleOptions || !connectionId || connectionId.trim() === "";

  const addEmail = (raw: string) => {
    const v = raw.trim().replace(/[,;]$/, "");
    if (!v) return;
    if (selected.includes(v)) return;
    onChange([...selected, v]);
    setDraft("");
  };

  const remove = (email: string) =>
    onChange(selected.filter((e) => e !== email));

  // Debounced people-search. Skipped entirely when the autocomplete can't
  // fire (no transport / no connection_id) so we don't burn requests during
  // the fallback banner.
  //
  // Race / cleanup contract (per the June 9, 2026 audit):
  //   - A fresh keystroke (or unmount) aborts the in-flight ``fetch`` via
  //     ``AbortController.abort()`` so a slow response can't overwrite
  //     the hits matching the LATEST query.
  //   - We still keep the local ``cancelled`` flag for the timer-pending
  //     branch (the request hasn't even started yet when the deps change),
  //     so we don't ``setBusy(false)`` against a stale render.
  React.useEffect(() => {
    if (!loadPeopleOptions || !open || !connectionId) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setBusy(true);
    const t = setTimeout(() => {
      const synthesisedField: ActionFieldSpec =
        field ?? {
          name: id ?? "recipients",
          label: ariaLabel ?? i18n._(msg`Destinatari`),
          type: "recipients",
        };
      void loadPeopleOptions({
        query: draft.trim(),
        connectionId,
        field: synthesisedField,
        signal: controller.signal,
      })
        .then((res) => {
          if (cancelled) return;
          setHits(res);
          // Fresh response → drop any stale keyboard-active highlight
          // BEFORE the new options paint, so ``aria-activedescendant``
          // never points to a row id that's about to disappear.
          setActiveIdx(-1);
        })
        .catch((err) => {
          // ``AbortError`` is the expected cancellation path — keep the
          // previous hits visible so the listbox doesn't flash empty
          // while the user types fast. Only clear on genuine failures.
          if (cancelled || (err && (err as Error).name === "AbortError")) return;
          setHits([]);
        })
        .finally(() => {
          if (!cancelled) setBusy(false);
        });
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(t);
      controller.abort();
    };
  }, [draft, open, loadPeopleOptions, connectionId, field, id, ariaLabel, i18n]);

  // Close on focus moving outside the container — plain ``focusin`` detector
  // is enough here because the dropdown is co-located with the input (no
  // portal). Effect attached only while ``open`` so we never run the
  // listener on idle mounts.
  React.useEffect(() => {
    if (!open) return;
    const onDocFocus = (ev: FocusEvent) => {
      const root = containerRef.current;
      if (!root) return;
      const target = ev.target as Node | null;
      if (target && root.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("focusin", onDocFocus);
    return () => document.removeEventListener("focusin", onDocFocus);
  }, [open]);

  // Memoised so the auto-reset effect below depends on a STABLE identity
  // (otherwise every render reconstructs the array and the effect runs
  // on every keystroke even when nothing changed).
  const filteredHits = React.useMemo(
    () => hits.filter((h) => !selected.includes(h.email)),
    [hits, selected],
  );
  const trimmedDraft = draft.trim();
  // Whether the typed text is a complete, addable email that isn't already a
  // chip or a listed hit — drives the explicit "Aggiungi «email»" dropdown row
  // so inviting someone outside the People-API results is always one click away.
  const canAddFree =
    EMAIL_RE.test(trimmedDraft) &&
    !selected.includes(trimmedDraft) &&
    !filteredHits.some((h) => h.email === trimmedDraft);
  const accountScopeLabel = !fallbackOnly ? connectionId : null;
  const effectivePlaceholder =
    placeholder ??
    (selected.length === 0
      ? fallbackOnly
        ? i18n._(msg`email@example.com — Invio per aggiungere`)
        : i18n._(msg`Cerca un contatto o digita un'email…`)
      : "");

  // Keep the keyboard-active option visible inside the listbox viewport
  // when the user pages through hits with ArrowUp / ArrowDown / Home /
  // End. ``scrollIntoView({block:"nearest"})`` is the modern primitive
  // for combobox panels and skips the jump-to-center jitter older
  // listbox patterns used to suffer.
  //
  // ``fieldId`` is included in the dep list because the DOM lookup
  // depends on it; ``activeIdx`` is the actual trigger. We don't need
  // the option-id helper itself — inlining the template here keeps the
  // effect's dependency graph honest (no eslint-disable needed).
  React.useEffect(() => {
    if (activeIdx < 0) return;
    const node = containerRef.current?.querySelector<HTMLElement>(
      `#${CSS.escape(`${fieldId}-people-opt-${activeIdx}`)}`,
    );
    node?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, fieldId]);

  const optionId = (idx: number) => `${fieldId}-people-opt-${idx}`;
  const activeDescendant =
    open && !fallbackOnly && activeIdx >= 0 && activeIdx < filteredHits.length
      ? optionId(activeIdx)
      : undefined;

  const commitHitAt = (idx: number) => {
    const hit = filteredHits[idx];
    if (!hit) return;
    addEmail(hit.email);
    inputRef.current?.focus();
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative flex flex-wrap items-center gap-1.5 rounded-md border border-hairline bg-card px-2 py-1.5",
        invalid && "border-destructive focus-within:ring-destructive",
        "focus-within:ring-2 focus-within:ring-ring",
        (disabled || readOnly) && "opacity-50",
        className,
      )}
    >
      <Search className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
      {selected.map((email) => (
        <span
          key={email}
          className="inline-flex items-center gap-1 rounded-full border border-hairline bg-white/80 px-2 py-0.5 text-[12px] text-ink-1"
        >
          <span className="max-w-[180px] truncate">{email}</span>
          {!disabled && !readOnly ? (
            <button
              type="button"
              aria-label={i18n._(msg`Rimuovi ${email}`)}
              className="text-ink-3 hover:text-ink-1"
              onClick={(e) => {
                e.stopPropagation();
                remove(email);
              }}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          ) : null}
        </span>
      ))}
      <input
        ref={inputRef}
        id={fieldId}
        type="text"
        inputMode="email"
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${fieldId}-people-listbox`}
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-activedescendant={activeDescendant}
        disabled={disabled || readOnly}
        value={draft}
        placeholder={effectivePlaceholder}
        className="min-w-[160px] flex-1 bg-transparent text-[13px] text-ink-1 placeholder:text-ink-3 focus:outline-none disabled:opacity-50"
        aria-invalid={invalid || undefined}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setDraft(e.target.value);
          // A fresh keystroke restarts the search debounce — the
          // highlight from the previous query is no longer meaningful.
          setActiveIdx(-1);
          if (!open) setOpen(true);
        }}
        onKeyDown={(e) => {
          const d = draft.trim();
          // A fully-typed email ALWAYS adds as a free recipient — even when
          // People-API hits are showing or one is hover-highlighted — so you
          // can invite anyone, not only contacts found by the search.
          if (
            (e.key === "Enter" ||
              e.key === "," ||
              e.key === ";" ||
              e.key === "Tab") &&
            d &&
            EMAIL_RE.test(d)
          ) {
            e.preventDefault();
            addEmail(d);
            return;
          }
          // Combobox keyboard nav (WAI-ARIA 1.2 listbox pattern): arrows
          // move the active descendant, Enter commits the active option
          // when one is highlighted (falls through to free-text commit
          // otherwise), Home / End jump to the edges.
          if (open && !fallbackOnly && filteredHits.length > 0) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveIdx((i) =>
                i + 1 >= filteredHits.length ? 0 : i + 1,
              );
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIdx((i) =>
                i <= 0 ? filteredHits.length - 1 : i - 1,
              );
              return;
            }
            if (e.key === "Home") {
              e.preventDefault();
              setActiveIdx(0);
              return;
            }
            if (e.key === "End") {
              e.preventDefault();
              setActiveIdx(filteredHits.length - 1);
              return;
            }
            if (e.key === "Enter" && activeIdx >= 0) {
              e.preventDefault();
              commitHitAt(activeIdx);
              return;
            }
          }
          if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab") {
            if (draft.trim()) {
              e.preventDefault();
              addEmail(draft);
            }
          } else if (e.key === "Backspace" && draft === "" && selected.length > 0) {
            remove(selected[selected.length - 1] ?? "");
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {busy && !fallbackOnly ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-ink-3" aria-hidden />
      ) : null}

      {open && fallbackOnly ? (
        <output className="absolute top-full right-0 left-0 z-50 mt-1 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/90 px-3 py-2 shadow-md ring-1 ring-amber-100">
          <Users className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" aria-hidden />
          <div className="flex flex-col gap-0.5 text-xs">
            <span className="font-medium text-amber-900">
              {i18n._(msg`Autocomplete contatti non attivo`)}
            </span>
            <span className="text-amber-800/90">
              {loadPeopleOptions
                ? i18n._(
                    msg`Scegli prima un account nel campo sopra per cercare tra i tuoi contatti. Per ora puoi digitare email libere e premere Invio.`,
                  )
                : i18n._(msg`Digita email libere e premi Invio per aggiungerle.`)}
            </span>
          </div>
        </output>
      ) : null}

      {open && !fallbackOnly ? (
        <div
          id={`${fieldId}-people-listbox`}
          role="listbox"
          aria-label={ariaLabel ?? i18n._(msg`Suggerimenti contatti`)}
          // ``tabIndex={-1}``: the listbox itself is NOT in the tab order
          // (the input keeps focus and drives the active descendant) but
          // it must be programmatically focusable to satisfy the lint
          // rule + the WAI-ARIA 1.2 listbox pattern.
          tabIndex={-1}
          className="absolute top-full right-0 left-0 z-50 mt-1 max-h-72 overflow-auto rounded-md border border-hairline bg-card shadow-lg ring-1 ring-black/5"
        >
          <div className="flex items-center gap-2 border-hairline border-b px-3 py-1.5 font-medium text-[10.5px] text-ink-3 uppercase tracking-wide">
            <Users className="h-3 w-3" aria-hidden />
            <span>{i18n._(msg`Suggerimenti`)}</span>
            {accountScopeLabel ? (
              <span className="ml-auto truncate font-normal text-ink-2 normal-case">
                {accountScopeLabel}
              </span>
            ) : null}
          </div>
          {/* Always-available free add — invite anyone, not just People-API hits. */}
          {canAddFree ? (
            <button
              type="button"
              // ``mousedown`` (not click) so the input doesn't blur-close the
              // panel before the handler runs — same pattern as the hit rows.
              onMouseDown={(e) => {
                e.preventDefault();
                addEmail(trimmedDraft);
              }}
              className="flex w-full items-center gap-2 border-hairline border-b px-3 py-1.5 text-left text-[13px] text-ink-1 hover:bg-accent/5"
            >
              <Plus className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden />
              <span className="truncate">
                <Trans>
                  Aggiungi <span className="font-medium">{trimmedDraft}</span>
                </Trans>
              </span>
            </button>
          ) : null}
          {busy && filteredHits.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-3 text-ink-3 text-xs">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              {i18n._(msg`Cerco tra i tuoi contatti…`)}
            </div>
          ) : filteredHits.length === 0 ? (
            <div className="flex flex-col gap-1 px-3 py-3 text-xs">
              <span className="text-ink-2">
                {draft.trim()
                  ? i18n._(msg`Nessun contatto trovato per "${draft.trim()}"`)
                  : i18n._(msg`Inizia a digitare il nome o l'email di una persona`)}
              </span>
              {draft.trim() ? (
                <span className="text-ink-3">
                  <Trans>
                    Premi <kbd className="rounded border border-hairline bg-white/80 px-1 py-px font-mono text-[10px]">Invio</kbd> per aggiungerla come email libera.
                  </Trans>
                </span>
              ) : null}
            </div>
          ) : (
            filteredHits.map((h, idx) => (
              // WAI-ARIA 1.2 combobox-listbox pattern: each option is a
              // ``role="option"`` element with a stable id, ``aria-selected``
              // mirroring the keyboard-active descendant, and pointer
              // commit on ``mousedown`` so the underlying ``<input>``
              // doesn't lose focus before the click registers.
              <div
                key={`${h.provider}:${h.email}`}
                id={optionId(idx)}
                role="option"
                aria-selected={idx === activeIdx}
                tabIndex={-1}
                onMouseEnter={() => setActiveIdx(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commitHitAt(idx);
                }}
                className={cn(
                  "flex w-full cursor-pointer items-center justify-between gap-3 px-3 py-1.5 text-left transition-colors",
                  idx === activeIdx
                    ? "bg-ando-orange-soft/60 text-ink-1"
                    : "hover:bg-accent/5",
                )}
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-medium text-[13px] text-ink-1">
                    {h.display_name || h.email}
                  </span>
                  <span className="truncate text-[11.5px] text-ink-3">
                    {h.email}
                    {h.job_title ? ` · ${h.job_title}` : ""}
                  </span>
                </span>
                <span
                  className={cn(
                    "shrink-0 rounded-full px-1.5 py-0.5 font-medium text-[9.5px] uppercase tracking-wide",
                    h.provider === "google" && "bg-[#4285F4]/10 text-[#4285F4]",
                    h.provider === "microsoft" && "bg-[#0078D4]/10 text-[#0078D4]",
                    h.provider === "other" && "bg-ink-3/10 text-ink-2",
                  )}
                >
                  {h.provider === "google"
                    ? "Google"
                    : h.provider === "microsoft"
                      ? "Microsoft"
                      : i18n._(msg`Altro`)}
                </span>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
};
RecipientsInput.displayName = "RecipientsInput";
